
import { Link } from 'react-router-dom';

export default function MultidisciplinaryCollaboration() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Article Header */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-full flex items-center justify-center mr-4">
                <i className="ri-user-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-teal-600 font-semibold">ビースト君</p>
                <p className="text-gray-500 text-sm">2025年10月4日</p>
              </div>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">薬剤師目線での多職種連携について</h1>
            <p className="text-xl text-gray-600">在宅医療チームの一員として、各職種との効果的な連携方法と薬剤師の役割を解説します</p>
          </div>
          <div className="flex justify-center">
            <img 
              src="https://public.readdy.ai/ai/img_res/11cdf26ba46f4bacbd965439a8c422e1.jpg"
              alt="多職種連携"
              className="w-full max-w-2xl h-80 object-cover object-top rounded-xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 mb-8">
              こんにちは、ビースト君です！在宅医療の現場では、様々な職種の専門家がチームを組んで患者さんをサポートしています。
              今日は薬剤師として、他職種との連携で大切にしていることや、効果的なチームワークについてお話しします。
              筋トレでもチームトレーニングが効果的なように、医療も連携が患者さんの最良の結果につながります！
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">多職種連携の重要性</h2>
            
            <p className="text-gray-700 mb-6">
              在宅医療では、患者さんの生活全体を支えるために、医療・介護・福祉の専門家が連携します。
              薬剤師は薬物療法の専門家として、他職種と情報共有し、患者さんにとって最適な治療環境を作り上げる重要な役割を担っています。
            </p>

            <div className="bg-blue-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-blue-800 mb-4">多職種連携のメリット</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🎯 患者さんへのメリット</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 包括的なケアの提供</li>
                    <li>• 重複や漏れの防止</li>
                    <li>• 生活の質（QOL）向上</li>
                    <li>• 安心・安全な療養環境</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">👥 医療従事者へのメリット</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 専門性の相互補完</li>
                    <li>• 負担の分散</li>
                    <li>• スキルアップの機会</li>
                    <li>• やりがいの向上</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🏥 医療システムへのメリット</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 医療費の適正化</li>
                    <li>• 入院期間の短縮</li>
                    <li>• 再入院率の低下</li>
                    <li>• 効率的な資源活用</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">👨‍👩‍👧‍👦 家族へのメリット</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 介護負担の軽減</li>
                    <li>• 専門的サポートの安心感</li>
                    <li>• 情報の一元化</li>
                    <li>• 緊急時の対応体制</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">在宅医との連携</h2>
            
            <p className="text-gray-700 mb-6">
              在宅医は在宅医療チームのリーダー的存在です。薬剤師として、医師との密な連携により、
              患者さんの病状変化に応じた適切な薬物療法を提供することが重要です。
            </p>

            <div className="bg-green-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-green-800 mb-4">在宅医との連携ポイント</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">📋 処方提案・疑義照会</h4>
                  <p className="text-gray-600 text-sm mb-2">薬学的知見に基づく積極的な処方提案と適切な疑義照会</p>
                  <div className="text-xs text-gray-500">
                    <p>• 薬物相互作用のチェックと代替薬提案</p>
                    <p>• 腎機能・肝機能に応じた用量調整提案</p>
                    <p>• 服薬しやすい剤形への変更提案</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">📊 服薬状況の報告</h4>
                  <p className="text-gray-600 text-sm mb-2">定期的な服薬状況と効果・副作用の詳細報告</p>
                  <div className="text-xs text-gray-500">
                    <p>• 残薬状況と服薬コンプライアンス</p>
                    <p>• 副作用の早期発見と報告</p>
                    <p>• 効果判定に必要な情報提供</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🚨 緊急時対応</h4>
                  <p className="text-gray-600 text-sm mb-2">24時間体制での薬物療法に関する緊急相談対応</p>
                  <div className="text-xs text-gray-500">
                    <p>• 薬物中毒・過量服薬時の対応</p>
                    <p>• 急変時の薬物療法サポート</p>
                    <p>• 休日・夜間の処方薬確保</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">📝 カンファレンス参加</h4>
                  <p className="text-gray-600 text-sm mb-2">定期的なチームカンファレンスでの薬学的視点の提供</p>
                  <div className="text-xs text-gray-500">
                    <p>• 治療方針決定への薬学的助言</p>
                    <p>• 薬物療法の評価と見直し提案</p>
                    <p>• 他職種への薬学情報提供</p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">訪問看護師との連携</h2>
            
            <p className="text-gray-700 mb-6">
              訪問看護師は患者さんと最も接触頻度が高く、日々の変化を把握している重要なパートナーです。
              薬剤師として、看護師からの情報を活かし、より良い薬物療法を提供することができます。
            </p>

            <div className="bg-pink-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-pink-800 mb-4">訪問看護師との連携ポイント</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">💊 服薬管理指導</h4>
                  <p className="text-gray-600 text-sm mb-2">看護師と連携した効果的な服薬管理方法の構築</p>
                  <div className="text-xs text-gray-500">
                    <p>• 一包化・お薬カレンダーの活用方法</p>
                    <p>• 服薬タイミングの生活リズムへの組み込み</p>
                    <p>• 家族への服薬支援指導</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">👀 副作用モニタリング</h4>
                  <p className="text-gray-600 text-sm mb-2">日常的な観察による副作用の早期発見体制</p>
                  <div className="text-xs text-gray-500">
                    <p>• 観察ポイントの共有と教育</p>
                    <p>• 副作用症状の評価基準統一</p>
                    <p>• 緊急性の判断基準の共有</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🩺 バイタルサインとの関連</h4>
                  <p className="text-gray-600 text-sm mb-2">バイタルサインの変化と薬物療法効果の関連評価</p>
                  <div className="text-xs text-gray-500">
                    <p>• 血圧・脈拍と循環器系薬剤の効果判定</p>
                    <p>• 体温と感染症治療薬の効果評価</p>
                    <p>• 呼吸状態と呼吸器系薬剤の調整</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🏠 在宅環境の評価</h4>
                  <p className="text-gray-600 text-sm mb-2">薬剤保管環境と服薬環境の最適化</p>
                  <div className="text-xs text-gray-500">
                    <p>• 薬剤保管場所の温度・湿度管理</p>
                    <p>• 誤薬防止のための環境整備</p>
                    <p>• 医療機器との薬剤相互作用確認</p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">ケアマネージャーとの連携</h2>
            
            <p className="text-gray-700 mb-6">
              ケアマネージャーは介護保険サービスの調整役として、患者さんの生活全体をコーディネートしています。
              薬剤師として、薬物療法が日常生活に与える影響を考慮したケアプラン作成に貢献します。
            </p>

            <div className="bg-yellow-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-yellow-800 mb-4">ケアマネージャーとの連携ポイント</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">📋 ケアプランへの薬学的視点</h4>
                  <p className="text-gray-600 text-sm mb-2">薬物療法を考慮したケアプラン作成への助言</p>
                  <div className="text-xs text-gray-500">
                    <p>• 服薬時間に合わせたサービス調整</p>
                    <p>• 薬剤の副作用を考慮した活動計画</p>
                    <p>• 認知機能低下時の服薬支援体制</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">💰 医療費・介護費の最適化</h4>
                  <p className="text-gray-600 text-sm mb-2">薬剤費を含めた総合的な費用対効果の検討</p>
                  <div className="text-xs text-gray-500">
                    <p>• ジェネリック医薬品への変更提案</p>
                    <p>• 重複処方の整理と費用削減</p>
                    <p>• 介護保険適用サービスとの調整</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">👨‍👩‍👧‍👦 家族支援</h4>
                  <p className="text-gray-600 text-sm mb-2">家族の介護負担軽減のための薬学的サポート</p>
                  <div className="text-xs text-gray-500">
                    <p>• 家族への服薬管理指導</p>
                    <p>• 緊急時の対応方法教育</p>
                    <p>• 介護者の精神的サポート</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🔄 サービス調整</h4>
                  <p className="text-gray-600 text-sm mb-2">他のサービス事業者との薬学情報共有</p>
                  <div className="text-xs text-gray-500">
                    <p>• デイサービスでの服薬管理連携</p>
                    <p>• ショートステイ時の薬剤情報提供</p>
                    <p>• 福祉用具と薬剤の相互作用確認</p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">言語聴覚士との連携</h2>
            
            <p className="text-gray-700 mb-6">
              言語聴覚士は嚥下機能や言語機能の専門家です。薬剤師として、嚥下困難な患者さんの服薬方法や、
              コミュニケーション障害のある患者さんへの服薬指導において、重要な連携パートナーです。
            </p>

            <div className="bg-purple-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-purple-800 mb-4">言語聴覚士との連携ポイント</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🥤 嚥下機能と服薬方法</h4>
                  <p className="text-gray-600 text-sm mb-2">嚥下機能評価に基づく最適な服薬方法の選択</p>
                  <div className="text-xs text-gray-500">
                    <p>• 錠剤の粉砕可否と代替剤形の提案</p>
                    <p>• とろみ剤との薬剤相互作用確認</p>
                    <p>• 嚥下訓練と服薬タイミングの調整</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">💬 コミュニケーション支援</h4>
                  <p className="text-gray-600 text-sm mb-2">言語障害のある患者さんへの効果的な服薬指導</p>
                  <div className="text-xs text-gray-500">
                    <p>• 視覚的ツールを活用した服薬説明</p>
                    <p>• 簡潔で理解しやすい指導方法</p>
                    <p>• 家族を通じた情報伝達体制</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🧠 認知機能と服薬管理</h4>
                  <p className="text-gray-600 text-sm mb-2">認知機能評価に基づく服薬管理方法の構築</p>
                  <div className="text-xs text-gray-500">
                    <p>• 記憶障害に対応した服薬支援ツール</p>
                    <p>• 段階的な服薬自立支援プログラム</p>
                    <p>• 認知機能改善薬の効果評価</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🎯 機能訓練との調整</h4>
                  <p className="text-gray-600 text-sm mb-2">リハビリテーションと薬物療法の相乗効果</p>
                  <div className="text-xs text-gray-500">
                    <p>• 訓練効果を高める薬剤の適切な使用</p>
                    <p>• 訓練時間と服薬タイミングの最適化</p>
                    <p>• 機能改善に応じた薬剤調整提案</p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">その他の職種との連携</h2>
            
            <p className="text-gray-700 mb-6">
              在宅医療チームには、理学療法士、作業療法士、管理栄養士、歯科衛生士など、
              様々な専門職が関わります。それぞれの専門性を活かした連携が重要です。
            </p>

            <div className="bg-indigo-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-indigo-800 mb-4">その他職種との連携例</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🏃 理学療法士</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 運動療法と薬物療法の相互作用</li>
                    <li>• 筋弛緩薬の効果評価</li>
                    <li>• 転倒リスク薬剤の管理</li>
                    <li>• リハビリ効果を高める薬剤調整</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">✋ 作業療法士</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• ADL向上のための薬剤調整</li>
                    <li>• 服薬動作の評価と改善</li>
                    <li>• 認知機能改善薬の効果判定</li>
                    <li>• 生活環境と薬剤保管の最適化</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🥗 管理栄養士</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 薬剤と食事の相互作用管理</li>
                    <li>• 栄養状態と薬剤効果の関連</li>
                    <li>• 食事制限と薬剤選択の調整</li>
                    <li>• サプリメントとの併用注意</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🦷 歯科衛生士</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 口腔ケアと薬剤の相互作用</li>
                    <li>• 口腔乾燥を起こす薬剤の管理</li>
                    <li>• 抗凝固薬使用時の口腔処置</li>
                    <li>• 口腔内崩壊錠の適応評価</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">効果的な連携のための工夫</h2>
            
            <p className="text-gray-700 mb-6">
              多職種連携を成功させるためには、コミュニケーションの質と情報共有の仕組みが重要です。
              筋トレでもフォームが大切なように、連携にも正しい「フォーム」があります。
            </p>

            <div className="bg-teal-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-teal-800 mb-4">連携を成功させるポイント</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">📱 情報共有ツールの活用</h4>
                  <p className="text-gray-600 text-sm mb-2">効率的で正確な情報共有システムの構築</p>
                  <div className="text-xs text-gray-500">
                    <p>• 電子カルテ・連携システムの活用</p>
                    <p>• 定期的な情報更新とアクセス管理</p>
                    <p>• 緊急時連絡体制の整備</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🤝 相互理解の促進</h4>
                  <p className="text-gray-600 text-sm mb-2">各職種の専門性と役割の理解</p>
                  <div className="text-xs text-gray-500">
                    <p>• 定期的な勉強会・研修会の開催</p>
                    <p>• 職種間の業務体験・見学</p>
                    <p>• 専門用語の共通理解</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">⏰ タイムリーな連携</h4>
                  <p className="text-gray-600 text-sm mb-2">適切なタイミングでの情報共有と相談</p>
                  <div className="text-xs text-gray-500">
                    <p>• 定期カンファレンスの効果的運営</p>
                    <p>• 緊急時の迅速な連絡体制</p>
                    <p>• 計画的な情報共有スケジュール</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🎯 患者中心のアプローチ</h4>
                  <p className="text-gray-600 text-sm mb-2">患者さんの価値観と希望を尊重した連携</p>
                  <div className="text-xs text-gray-500">
                    <p>• 患者・家族の意向確認と共有</p>
                    <p>• 生活の質を重視した目標設定</p>
                    <p>• 文化的背景への配慮</p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">連携における課題と解決策</h2>
            
            <p className="text-gray-700 mb-6">
              多職種連携には様々な課題がありますが、それぞれに対する具体的な解決策があります。
              課題を乗り越えることで、より強固なチームワークが生まれます。
            </p>

            <div className="bg-red-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-red-800 mb-4">よくある課題と解決策</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">⚠️ 情報共有の不備</h4>
                  <p className="text-gray-600 text-sm mb-2">課題：情報の伝達漏れや遅延、重複</p>
                  <div className="text-xs text-gray-500">
                    <p>解決策：標準化された情報共有フォーマットの使用</p>
                    <p>解決策：定期的な情報更新ルールの設定</p>
                    <p>解決策：ITツールを活用した自動化</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🕐 時間調整の困難</h4>
                  <p className="text-gray-600 text-sm mb-2">課題：各職種のスケジュール調整が困難</p>
                  <div className="text-xs text-gray-500">
                    <p>解決策：オンライン会議システムの活用</p>
                    <p>解決策：非同期的な情報共有方法の導入</p>
                    <p>解決策：代理出席システムの構築</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">💭 価値観の相違</h4>
                  <p className="text-gray-600 text-sm mb-2">課題：職種間での治療方針や優先順位の違い</p>
                  <div className="text-xs text-gray-500">
                    <p>解決策：患者中心の共通目標設定</p>
                    <p>解決策：定期的な価値観共有セッション</p>
                    <p>解決策：エビデンスに基づく議論の促進</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">📚 専門知識の格差</h4>
                  <p className="text-gray-600 text-sm mb-2">課題：各職種の専門領域への理解不足</p>
                  <div className="text-xs text-gray-500">
                    <p>解決策：相互教育プログラムの実施</p>
                    <p>解決策：専門用語集の作成と共有</p>
                    <p>解決策：症例検討会での学習機会提供</p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">まとめ</h2>
            
            <p className="text-gray-700 mb-6">
              多職種連携は、患者さんにとって最良の医療・介護を提供するための重要な仕組みです。
              薬剤師として、薬学的専門性を活かしながら、他職種との効果的な連携を心がけることが大切です。
            </p>

            <div className="bg-teal-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-teal-800 mb-4">ビースト君からのメッセージ</h3>
              <p className="text-gray-700">
                筋トレでも一人より仲間と一緒の方が頑張れるように、医療も多職種で連携することで、
                一人では成し得ない大きな成果を生み出せます。患者さんの笑顔のために、
                それぞれの専門性を活かしながら、チーム一丸となって頑張りましょう！
                薬剤師として、皆さんの架け橋になれるよう努力し続けます💪🤝
              </p>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">関連記事</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Link to="/beast-blog/winter-mental-health" className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100 overflow-hidden cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=Muscular%20anime%20pharmacist%20character%20with%20black%20two-block%20haircut%20explaining%20winter%20mental%20health%20care%2C%20cartoon%20illustration%20with%20seasonal%20depression%20awareness%2C%20cozy%20winter%20setting%20with%20warm%20lighting%2C%20educational%20healthcare%20content%20about%20mental%20wellness&width=400&height=250&seq=beast-winter-mental-health-related&orientation=landscape"
                alt="冬季のメンタルヘルス対策"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">冬季のメンタルヘルス対策について</h3>
                <p className="text-gray-600">寒い季節に起こりやすい心の不調と、薬剤師が教える効果的な対策方法をご紹介します</p>
              </div>
            </Link>
            <Link to="/beast-blog/health-management" className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100 overflow-hidden cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=Muscular%20anime%20pharmacist%20character%20with%20black%20two-block%20haircut%20demonstrating%20exercise%20and%20health%20tips%2C%20cartoon%20illustration%20showing%20workout%20and%20wellness%20advice%2C%20friendly%20educational%20content%2C%20colorful%20gym%20and%20pharmacy%20background&width=400&height=250&seq=beast-health-management-related3&orientation=landscape"
                alt="健康管理"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">薬剤師が教える筋トレと健康管理</h3>
                <p className="text-gray-600">薬学知識と筋トレ経験を活かした健康管理方法をお伝えします</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">患者さん向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">健康サポート</Link></li>
                <li><Link to="/self-care" className="hover:text-white cursor-pointer">セルフケア</Link></li>
                <li><Link to="/otc" className="hover:text-white cursor-pointer">OTC医薬品</Link></li>
                <li><Link to="/kampo" className="hover:text-white cursor-pointer">漢方相談</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/pharmacist-resources" className="hover:text-white cursor-pointer">専門リソース</Link></li>
                <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                <li><Link to="/home-care" className="hover:text-white cursor-pointer">在宅医療</Link></li>
                <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/guidelines" className="hover:text-white cursor-pointer">疾患ガイドライン</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
